import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import type {
	Reflection,
	ContributingFactor,
	Signal,
	CorrectiveAction,
} from "../types";

interface UseReflectionDataReturn {
	reflection: Reflection | null;
	contributingFactors: ContributingFactor[];
	signals: Signal[];
	correctiveActions: CorrectiveAction[];
	loading: boolean;
	error: string | null;
	refetch: () => Promise<void>;
	setReflection: React.Dispatch<React.SetStateAction<Reflection | null>>;
}

export function useReflectionData(
	reflectionId: string | undefined,
	userId: string | undefined,
): UseReflectionDataReturn {
	const [reflection, setReflection] = useState<Reflection | null>(null);
	const [contributingFactors, setContributingFactors] = useState<
		ContributingFactor[]
	>([]);
	const [signals, setSignals] = useState<Signal[]>([]);
	const [correctiveActions, setCorrectiveActions] = useState<
		CorrectiveAction[]
	>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const fetchReflectionData = async () => {
		if (!reflectionId || !userId) {
			setError("Missing reflection ID or user ID");
			setLoading(false);
			return;
		}

		try {
			setLoading(true);
			setError(null);

			const { data: reflectionData, error: reflectionError } = await supabase
				.from("reflections")
				.select("*")
				.eq("id", reflectionId)
				.eq("user_id", userId)
				.single();

			if (reflectionError) {
				if (reflectionError.code === "PGRST116") {
					setError("Reflection not found");
				} else {
					setError(reflectionError.message);
				}
				return;
			}

			setReflection(reflectionData);

			const [factorsResult, signalsResult, actionsResult] =
				await Promise.allSettled([
					supabase
						.from("contributing_factors")
						.select("*")
						.eq("reflection_id", reflectionId)
						.order("created_at", { ascending: true }),
					supabase
						.from("signals")
						.select("*")
						.eq("reflection_id", reflectionId)
						.order("created_at", { ascending: true }),
					supabase
						.from("corrective_actions")
						.select("*")
						.eq("reflection_id", reflectionId)
						.order("priority", { ascending: false }),
				]);

			if (factorsResult.status === "fulfilled" && !factorsResult.value.error) {
				setContributingFactors(factorsResult.value.data || []);
			}

			if (signalsResult.status === "fulfilled" && !signalsResult.value.error) {
				setSignals(signalsResult.value.data || []);
			}

			if (actionsResult.status === "fulfilled" && !actionsResult.value.error) {
				setCorrectiveActions(actionsResult.value.data || []);
			}
		} catch (error) {
			console.error("Error fetching reflection data:", error);
			setError("Failed to load reflection data");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchReflectionData();
	}, [reflectionId, userId]);

	return {
		reflection,
		contributingFactors,
		signals,
		correctiveActions,
		loading,
		error,
		refetch: fetchReflectionData,
		setReflection,
	};
}
